import React, { useState } from "react";
import { ALERTS, BRANCHES } from "../lib/data";
import { Tile, Badge, SectionHeader, StatusDot } from "../components/Primitives";
import { formatPeso, cn } from "../lib/utils";
import { TrendingUp, TrendingDown, AlertOctagon, Activity, Zap } from "lucide-react";
import AnomalyInvestigation from "../components/AnomalyInvestigation";

const ANOMALIES = [
  {
    id: "A-001",
    type: "Demand Spike",
    severity: "info",
    title: "Freon R410A demand +38% in Pampanga (24h)",
    drivers: ["Heat advisory in CL", "Construction activity uptick"],
    confidence: 0.94,
    branches: 6,
    impact: 2_400_000,
    detectedAt: "2026-01-15 06:42",
    method: "Z-score 3.2σ on rolling 28-day mean",
    affectedSkus: ["Freon R410A 11.3kg", "Freon R32 9.5kg", "Inverter Compressor 1.5HP"],
    affectedBranches: ["Mexico Pampanga", "San Fernando Pampanga", "Tarlac", "Olongapo", "Subic", "Mexico Bulacan"],
    timeline: [
      { t: "−72h", evt: "PAGASA issues yellow heat advisory for Central Luzon" },
      { t: "−48h", evt: "Demand at Mexico Pampanga begins drifting +12%" },
      { t: "−24h", evt: "Cluster spike detected across 6 Pampanga/Zambales branches" },
      { t: "now", evt: "Inventory cover on R410A drops below 7 days at 4 of 6 branches" },
    ],
    relatedKpis: { "Forecast accuracy (R410A)": "−6.4pp wk/wk", "Stockout rate (PMP)": "+2.1pp wk/wk", "Shopee R410A views": "+44%" },
  },
  {
    id: "A-002",
    type: "Demand Drop",
    severity: "warning",
    title: "Auto compressor sales −22% in Quezon Province",
    drivers: ["LTO registrations −4% in region", "Competitor promo at Lipa"],
    confidence: 0.81,
    branches: 4,
    impact: -1_840_000,
    detectedAt: "2026-01-14 19:18",
    method: "Isolation Forest multivariate outlier",
    affectedSkus: ["Sanden SD7H15 Compressor", "OEM Toyota Vios Compressor", "Capella Auto Compressor TM-16", "Magnetic Clutch — Mitsubishi"],
    affectedBranches: ["Lipa Batangas", "Batangas City", "Lucena Diversion", "Lucena Quezon Ave"],
    timeline: [
      { t: "−14d", evt: "LTO January registrations published — Quezon −4.1% YoY" },
      { t: "−9d", evt: "Competitor (CoolMax Auto) launches 18% off on Sanden equivalents" },
      { t: "−5d", evt: "Lipa walk-in conversion drops from 36% to 27%" },
      { t: "−2d", evt: "Lucena Diversion misses daily target 4 days running" },
    ],
    relatedKpis: { "Branch achievement (Quezon)": "82% (was 96%)", "AOV": "−₱340", "Shopee orders (Quezon)": "+11%" },
  },
  {
    id: "A-003",
    type: "Stockout Cluster",
    severity: "critical",
    title: "Inverter compressor stockouts trending across South Luzon",
    drivers: ["Demand surge", "Capella shipment delay"],
    confidence: 0.96,
    branches: 9,
    impact: -3_280_000,
    detectedAt: "2026-01-15 05:21",
    method: "Forecast residual >2× CI band on A-class SKU",
    affectedSkus: ["Inverter Compressor 1.5HP", "Inverter Compressor 1.0HP", "Running Capacitor 35µF"],
    affectedBranches: ["Calamba", "Sta. Rosa", "Tagaytay", "San Pablo Laguna", "Sta. Cruz Laguna", "Lipa Batangas", "Batangas City", "Tanza Cavite", "General Trias"],
    timeline: [
      { t: "−21d", evt: "Capella confirms 14-day shipment delay (port congestion)" },
      { t: "−10d", evt: "Inverter 1.5HP forecast revised +24%" },
      { t: "−4d", evt: "Calamba goes to zero stock on Inverter 1.5HP" },
      { t: "−1d", evt: "5 more SL branches fall below 3-day cover" },
      { t: "now", evt: "9 branches at <2-day cover; 3 fully stocked-out" },
    ],
    relatedKpis: { "A-class stockout rate (SL)": "9.4% (target <5%)", "Lost sales est.": "₱3.28M / wk", "Customer escalations": "+18" },
  },
  {
    id: "A-004",
    type: "Margin Erosion",
    severity: "warning",
    title: "Shopee category margin down 320 bps wk/wk",
    drivers: ["Free-shipping voucher uptake", "Aggressive competitor discounting"],
    confidence: 0.88,
    branches: 0,
    impact: -680_000,
    detectedAt: "2026-01-14 22:05",
    method: "Margin decomposition — voucher cost > peer benchmark",
    affectedSkus: ["Running Capacitor 35µF", "Fan Capacitor 6µF", "Foam Filter Roll", "Universal Aircon Control PCB"],
    affectedBranches: [],
    timeline: [
      { t: "−14d", evt: "Shopee 1.15 voucher campaign launches platform-wide" },
      { t: "−7d", evt: "Competitor 'AirCool PH' undercuts capacitor SKUs by 12%" },
      { t: "−3d", evt: "Voucher uptake on 1Rotary listings climbs 41% wk/wk" },
      { t: "now", evt: "Blended Shopee GM at 24.6% vs 27.8% prior week" },
    ],
    relatedKpis: { "Shopee GM": "24.6% (−320 bps)", "Voucher attach rate": "62%", "Orders": "+9%" },
  },
  {
    id: "A-005",
    type: "Channel Shift",
    severity: "info",
    title: "Direct-channel volume +18% — corporate fleet bookings",
    drivers: ["3 new corporate accounts onboarded"],
    confidence: 0.92,
    branches: 12,
    impact: 4_800_000,
    detectedAt: "2026-01-13 14:30",
    method: "Channel mix delta + customer-segment classifier",
    affectedSkus: ["Sanden SD7H15 Compressor", "Mitsubishi Adventure Blower Motor", "Freon R134a 13.6kg", "Auxiliary Fan — Honda CR-V"],
    affectedBranches: ["Bacoor Cavite", "Parañaque Sucat", "Makati Chino Roces", "Pasig Ortigas", "Taguig FTI", "Mandaluyong Shaw", "Quezon City Cubao", "Manila Quiapo", "Caloocan", "Valenzuela", "Pasay Roxas", "Las Piñas Alabang"],
    timeline: [
      { t: "−30d", evt: "Grab Fleet account signed — 280 vehicle target" },
      { t: "−18d", evt: "JG Summit motor-pool extension confirmed" },
      { t: "−10d", evt: "MoveIt Logistics pilot at 4 NCR branches" },
      { t: "now", evt: "Direct channel volume +18% MoM, AOV ₱4,476" },
    ],
    relatedKpis: { "Direct GM": "34.1%", "Repeat rate": "78%", "Days sales outstanding": "32" },
  },
  {
    id: "A-006",
    type: "Supplier Risk",
    severity: "critical",
    title: "Honeywell OTD trending below 75% — 6-month low",
    drivers: ["Port congestion in source country", "Capacity bottleneck"],
    confidence: 0.91,
    branches: 0,
    impact: -5_200_000,
    detectedAt: "2026-01-12 09:00",
    method: "Rolling 90-day OTD trend + supplier risk score",
    affectedSkus: ["Freon R32 9.5kg", "Freon R410A 11.3kg", "Freon R134a 13.6kg"],
    affectedBranches: [],
    timeline: [
      { t: "−90d", evt: "Honeywell OTD: 89%" },
      { t: "−60d", evt: "Honeywell OTD: 82%" },
      { t: "−30d", evt: "Honeywell OTD: 78%" },
      { t: "−7d", evt: "Honeywell OTD: 74% — first time below threshold in 18 months" },
      { t: "now", evt: "3 critical refrigerant SKUs at <30 day cover network-wide" },
    ],
    relatedKpis: { "Network refrigerant cover": "28 days (was 56)", "Open PO value at risk": "₱14.3M", "Alternate supplier capacity": "60% of need" },
  },
  {
    id: "A-007",
    type: "Inventory Build",
    severity: "warning",
    title: "Door gasket overstock building in 14 branches",
    drivers: ["Seasonal demand softening", "Last reorder over-quantified"],
    confidence: 0.86,
    branches: 14,
    impact: -1_240_000,
    detectedAt: "2026-01-11 16:42",
    method: "Days-on-hand >2× target across cluster",
    affectedSkus: ["Door Gasket Magnetic", "Refrig Door Gasket 24in", "Foam Filter Roll"],
    affectedBranches: ["Davao City", "Tagum", "Butuan", "Cagayan de Oro", "General Santos", "Iligan", "Zamboanga", "Cotabato", "Cebu City", "Mandaue Cebu", "Lapu-Lapu", "Iloilo City", "Bacolod", "Tacloban Leyte"],
    timeline: [
      { t: "−60d", evt: "Auto-reorder triggered at 4.2K units across Mindanao+Visayas" },
      { t: "−40d", evt: "Demand softens 18% — repair-shop activity dip post-typhoon" },
      { t: "−14d", evt: "DOH crosses 90 days at 8 branches" },
      { t: "now", evt: "14 branches at 110–148 days on hand; ₱1.24M working capital tied" },
    ],
    relatedKpis: { "DOH (gaskets)": "128 days avg", "Inventory turn (gaskets)": "2.9× (target 6×)", "WC tied": "₱1.24M" },
  },
];

const STATS = {
  total: ANOMALIES.length,
  critical: ANOMALIES.filter(a => a.severity === "critical").length,
  warning: ANOMALIES.filter(a => a.severity === "warning").length,
  info: ANOMALIES.filter(a => a.severity === "info").length,
};

export default function Anomalies() {
  const [active, setActive] = useState(null);
  const [snoozed, setSnoozed] = useState({});
  const visible = ANOMALIES.filter(a => !snoozed[a.id]);
  return (
    <div className="space-y-6 animate-fade-in">
      <SectionHeader eyebrow="PART V · AI Anomaly Layer" title="Anomaly Detection"
        description="Auto-surfaced unusual patterns across demand, supply, channel and margin — ranked by impact." />

      <div className="tile grid grid-cols-2 md:grid-cols-5 divide-x divide-border">
        <Agg label="Active Anomalies" value={STATS.total} />
        <Agg label="Critical" value={STATS.critical} status="critical" />
        <Agg label="Warning" value={STATS.warning} status="warning" />
        <Agg label="Informational" value={STATS.info} status="info" />
        <Agg label="Model Confidence Avg" value="89.4%" status="success" />
      </div>

      <div className="grid grid-cols-12 gap-6">
        <Tile className="col-span-12 lg:col-span-8" testid="anomaly-feed" title="Live Anomaly Feed" action={<Badge status="neutral">{visible.length} active</Badge>}>
          <div className="space-y-0 divide-y divide-border -mx-4 -my-4">
            {visible.map(a => (
              <div key={a.id} className="px-4 py-4 flex gap-4 hover:bg-surface-2 transition-colors">
                <div className="shrink-0">
                  <div className={cn("w-9 h-9 rounded-sm border flex items-center justify-center",
                    a.severity === "critical" && "bg-critical/10 border-critical/30 text-critical",
                    a.severity === "warning" && "bg-warning/10 border-warning/30 text-warning",
                    a.severity === "info" && "bg-info/10 border-info/30 text-info")}>
                    {a.severity === "critical" ? <AlertOctagon size={16} /> : a.type.includes("Drop") || a.impact < 0 ? <TrendingDown size={16} /> : <TrendingUp size={16} />}
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge status={a.severity}>{a.severity}</Badge>
                    <span className="text-[11px] uppercase tracking-widest text-muted">{a.type}</span>
                    <span className="text-[11px] text-subtle font-mono-tabular">{a.id}</span>
                  </div>
                  <h3 className="font-display text-base font-semibold mt-1.5">{a.title}</h3>
                  <div className="text-[11px] text-muted mt-1">
                    <span className="uppercase tracking-widest">Drivers:</span>
                    <span className="ml-2">{a.drivers.join(" · ")}</span>
                  </div>
                  <div className="mt-2.5 flex flex-wrap items-center gap-x-5 gap-y-1 text-xs">
                    <Stat label="Confidence" value={`${(a.confidence * 100).toFixed(0)}%`} />
                    <Stat label="Branches" value={a.branches} />
                    <Stat label="Impact" value={formatPeso(a.impact, { compact: true })} highlight={a.impact < 0} />
                  </div>
                </div>
                <div className="shrink-0 flex flex-col gap-1.5">
                  <button data-testid={`investigate-${a.id}`}
                          onClick={() => setActive(a)}
                          className="text-[11px] px-3 py-1.5 bg-primary text-primary-fg rounded-sm hover:opacity-90 inline-flex items-center gap-1.5 font-semibold">
                    <Zap size={11} />
                    Investigate
                  </button>
                  <button data-testid={`snooze-${a.id}`}
                          onClick={() => setSnoozed(s => ({ ...s, [a.id]: true }))}
                          className="text-[11px] px-2.5 py-1 border border-border text-muted hover:text-fg rounded-sm">
                    Snooze
                  </button>
                </div>
              </div>
            ))}
            {visible.length === 0 && (
              <div className="px-4 py-12 text-center text-muted text-sm">
                All anomalies cleared. Check back during the next AI run cycle.
              </div>
            )}
          </div>
        </Tile>

        <Tile className="col-span-12 lg:col-span-4" title="Detection Methodology" testid="methodology">
          <div className="space-y-4 text-sm">
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-sm bg-primary/10 text-primary flex items-center justify-center shrink-0"><Activity size={14} /></div>
              <div>
                <div className="font-display font-semibold">Z-Score on Rolling Mean</div>
                <p className="text-[11px] text-muted mt-1">|x − μ_28d| / σ_28d &gt; 2.5 flags demand/sales anomalies per SKU × Branch × Channel.</p>
              </div>
            </div>
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-sm bg-primary/10 text-primary flex items-center justify-center shrink-0"><Zap size={14} /></div>
              <div>
                <div className="font-display font-semibold">Isolation Forest</div>
                <p className="text-[11px] text-muted mt-1">Multi-variate outlier detection for combinations of stockout rate × revenue × margin.</p>
              </div>
            </div>
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-sm bg-primary/10 text-primary flex items-center justify-center shrink-0"><TrendingUp size={14} /></div>
              <div>
                <div className="font-display font-semibold">Forecast Residual</div>
                <p className="text-[11px] text-muted mt-1">When actual deviates from forecast by &gt;2× CI width, an anomaly is fired.</p>
              </div>
            </div>
          </div>
          <hr className="my-4 border-border" />
          <div className="text-[11px] text-muted leading-relaxed">
            <strong className="text-fg">Routing:</strong> Anomalies route to the Org Topology layer of the Digital Twin — branch-level → Branch Head; SKU-level → Demand Planning; supplier-level → Merchandising.
          </div>
        </Tile>
      </div>

      <AnomalyInvestigation anomaly={active} onClose={() => setActive(null)} />
    </div>
  );
}

function Agg({ label, value, status }) {
  return (
    <div className="px-4 py-3">
      <div className="kpi-label flex items-center gap-2">{status && <StatusDot status={status} />}{label}</div>
      <div className="kpi-value text-2xl mt-1.5">{value}</div>
    </div>
  );
}
function Stat({ label, value, highlight }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="kpi-label">{label}</span>
      <span className={cn("font-mono-tabular font-semibold", highlight && "text-critical")}>{value}</span>
    </div>
  );
}
